import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateReservationRequestSchema,
  RULE_LABELS,
  toIsoInstant,
  type AirshipWithAvailability,
  type CreateReservationRequest,
} from '@swi/contracts';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as api from '../api/endpoints';
import { isApiError } from '../api/problem';
import { PageHeader } from '../components/layout/PageHeader';
import { AvailabilityCalendar } from '../components/reservations/AvailabilityCalendar';
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
  Textarea,
  buttonClass,
  cx,
} from '../components/ui/primitives';
import { dateTime, duration, money, moneyExact, toLocalInputValue } from '../lib/format';

const STEPS = ['Vzducholoď', 'Trasa a termín', 'Pasažéři', 'Souhrn'] as const;

/**
 * Reservation wizard.
 *
 * The payload is validated with CreateReservationRequestSchema — the very schema
 * the API validates the request with — so the form cannot accept something the
 * backend would reject on shape. Business rules (overlap, turnaround, capacity)
 * are checked live against /availability before the user gets to the summary.
 */
export function NewReservationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [airshipId, setAirshipId] = useState<number | null>(
    searchParams.get('airshipId') === null ? null : Number(searchParams.get('airshipId')),
  );
  const [departureHubId, setDepartureHubId] = useState<number | null>(null);
  const [arrivalHubId, setArrivalHubId] = useState<number | null>(null);
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [pilotId, setPilotId] = useState<number | null>(null);
  const [purpose, setPurpose] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const airships = useQuery({ queryKey: ['airships'], queryFn: () => api.listAirships() });
  const hubs = useQuery({ queryKey: ['hubs'], queryFn: api.listHubs });
  const pilots = useQuery({ queryKey: ['pilots'], queryFn: api.listPilots });

  const airship = useMemo<AirshipWithAvailability | null>(
    () => airships.data?.find((candidate) => candidate.id === airshipId) ?? null,
    [airships.data, airshipId],
  );

  // Preselect the home hub as departure once an airship is chosen.
  const window = useMemo(() => {
    if (startLocal === '' || endLocal === '') {
      return null;
    }
    const start = new Date(startLocal);
    const end = new Date(endLocal);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      return null;
    }
    return { from: toIsoInstant(start), to: toIsoInstant(end) };
  }, [startLocal, endLocal]);

  const availability = useQuery({
    queryKey: ['availability', airshipId, window?.from, window?.to, passengers],
    queryFn: () =>
      api.checkAvailability({
        airshipId: airshipId!,
        from: window!.from,
        to: window!.to,
        passengers,
      }),
    enabled: airshipId !== null && window !== null,
  });

  const priceQuote = useQuery({
    queryKey: ['quote', airshipId, window?.from, window?.to, passengers],
    queryFn: () =>
      api.quote({
        airshipId: airshipId!,
        startAt: window!.from,
        endAt: window!.to,
        passengers,
      }),
    enabled: airshipId !== null && window !== null,
  });

  const create = useMutation({
    mutationFn: (body: CreateReservationRequest) => api.createReservation(body),
    onSuccess: (reservation) => {
      toast.success('Rezervace vytvořena jako koncept', {
        description: 'Operátor flotily ji potvrdí, nebo ti napíše, co upravit.',
      });
      navigate(`/reservations/${reservation.id}`);
    },
    onError: (error: unknown) => {
      if (isApiError(error)) {
        setFieldErrors(error.fieldErrors);
        toast.error(error.humanMessage, { description: error.technicalDetail });
      } else {
        toast.error(error instanceof Error ? error.message : 'Rezervaci nelze vytvořit');
      }
    },
  });

  const payload = useMemo(() => {
    if (airshipId === null || departureHubId === null || arrivalHubId === null || window === null) {
      return null;
    }
    return {
      airshipId,
      departureHubId,
      arrivalHubId,
      startAt: window.from,
      endAt: window.to,
      passengers,
      ...(pilotId === null ? {} : { pilotId }),
      ...(purpose.trim() === '' ? {} : { purpose: purpose.trim() }),
    };
  }, [airshipId, departureHubId, arrivalHubId, window, passengers, pilotId, purpose]);

  const parsed = payload === null ? null : CreateReservationRequestSchema.safeParse(payload);

  const canAdvance = (): boolean => {
    if (step === 0) return airshipId !== null;
    if (step === 1) {
      return (
        departureHubId !== null &&
        arrivalHubId !== null &&
        window !== null &&
        availability.data?.available === true
      );
    }
    if (step === 2) return passengers >= 1 && parsed?.success === true;
    return true;
  };

  const submit = () => {
    if (parsed?.success !== true) {
      return;
    }
    setFieldErrors({});
    create.mutate(parsed.data);
  };

  return (
    <>
      <PageHeader
        title="Nová rezervace"
        description="Vyber vzducholoď, termín a trasu. Dostupnost se kontroluje průběžně, včetně povinné pozemní obsluhy mezi lety."
        actions={
          <Link to="/fleet" className={buttonClass('ghost', 'sm')}>
            ← Flotila
          </Link>
        }
      />

      {/* Stepper */}
      <ol className="mb-5 flex flex-wrap gap-2">
        {STEPS.map((label, index) => (
          <li key={label}>
            <button
              type="button"
              onClick={() => {
                if (index < step) setStep(index);
              }}
              disabled={index > step}
              className={cx(
                'flex items-center gap-2 rounded-[var(--radius-full)] px-3 py-1.5 text-[13px] transition-colors',
                index === step
                  ? 'bg-[var(--accent-dim)] font-medium text-[var(--accent)]'
                  : index < step
                    ? 'text-[var(--text-secondary)] hover:bg-[var(--surface-glass-strong)]'
                    : 'text-[var(--text-muted)]',
              )}
            >
              <span
                className="mono grid size-5 place-items-center rounded-full text-[11px]"
                style={{
                  background: index <= step ? 'var(--accent)' : 'var(--surface-glass-strong)',
                  color: index <= step ? 'var(--text-inverse)' : 'var(--text-muted)',
                }}
              >
                {index + 1}
              </span>
              {label}
            </button>
          </li>
        ))}
      </ol>

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <motion.div key={step} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
          {step === 0 && (
            <Card>
              <CardHeader title="Která vzducholoď" subtitle="Vyřazené a servisované se nezobrazují." />
              {airships.isLoading ? (
                <Skeleton className="h-40" />
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {airships.data
                    ?.filter((candidate) => candidate.status === 'ACTIVE')
                    .map((candidate) => (
                      <button
                        key={candidate.id}
                        type="button"
                        onClick={() => {
                          setAirshipId(candidate.id);
                          setDepartureHubId(candidate.homeHub.id);
                          setArrivalHubId((current) => current ?? candidate.homeHub.id);
                          setPassengers((current) => Math.min(current, candidate.capacityPax));
                        }}
                        className={cx(
                          'rounded-[var(--radius)] border p-3 text-left transition-colors',
                          candidate.id === airshipId
                            ? 'border-[var(--accent)] bg-[var(--accent-dim)]'
                            : 'border-[var(--border-subtle)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-glass-strong)]',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{candidate.name}</span>
                          <span className="mono text-[11px] text-[var(--accent)]">
                            {candidate.registration}
                          </span>
                        </div>
                        <p className="mono mt-1 text-[11px] text-[var(--text-secondary)]">
                          {candidate.capacityPax} pax · třída {candidate.airshipClass} ·{' '}
                          {money(candidate.hourlyRateCzk)}/h
                        </p>
                        <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                          turnaround {candidate.turnaroundMinutes} min · hub{' '}
                          {candidate.homeHub.code}
                        </p>
                      </button>
                    ))}
                </div>
              )}
            </Card>
          )}

          {step === 1 && airship !== null && (
            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader title="Trasa a termín" subtitle={`${airship.name} · ${airship.registration}`} />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Hub odletu">
                    <Select
                      value={departureHubId ?? ''}
                      onChange={(event) => setDepartureHubId(Number(event.target.value))}
                    >
                      {hubs.data?.map((hub) => (
                        <option key={hub.id} value={hub.id}>
                          {hub.code} — {hub.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Hub příletu" hint="Pro okružní let vyber stejný hub.">
                    <Select
                      value={arrivalHubId ?? ''}
                      onChange={(event) => setArrivalHubId(Number(event.target.value))}
                    >
                      {hubs.data?.map((hub) => (
                        <option key={hub.id} value={hub.id}>
                          {hub.code} — {hub.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field
                    label="Začátek"
                    error={fieldErrors.startAt?.[0]}
                    hint="Nejdřív 2 h, nejdéle 14 dní."
                  >
                    <Input
                      type="datetime-local"
                      value={startLocal}
                      min={toLocalInputValue(new Date(Date.now() + 3600_000))}
                      onChange={(event) => setStartLocal(event.target.value)}
                    />
                  </Field>

                  <Field label="Konec" error={fieldErrors.endAt?.[0]}>
                    <Input
                      type="datetime-local"
                      value={endLocal}
                      min={startLocal === '' ? undefined : startLocal}
                      onChange={(event) => setEndLocal(event.target.value)}
                    />
                  </Field>
                </div>

                {window !== null && (
                  <div className="mt-3">
                    <AvailabilityVerdict
                      loading={availability.isFetching}
                      available={availability.data?.available}
                      violations={availability.data?.violations ?? []}
                      conflicts={availability.data?.conflicts ?? []}
                      turnaroundMinutes={availability.data?.turnaroundMinutes ?? 0}
                    />
                  </div>
                )}
              </Card>

              <Card>
                <CardHeader
                  title="Obsazenost vzducholodě"
                  subtitle="Tvůj termín je vyznačený přerušovaně."
                />
                <AvailabilityCalendar
                  airshipId={airship.id}
                  from={toIsoInstant(new Date())}
                  to={toIsoInstant(new Date(Date.now() + 21 * 24 * 3600_000))}
                  highlight={window}
                />
              </Card>
            </div>
          )}

          {step === 2 && airship !== null && (
            <Card>
              <CardHeader title="Pasažéři a posádka" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label={`Počet pasažérů (max ${String(airship.capacityPax)})`}
                  error={fieldErrors.passengers?.[0]}
                >
                  <Input
                    type="number"
                    min={1}
                    max={airship.capacityPax}
                    value={passengers}
                    onChange={(event) => setPassengers(Number(event.target.value))}
                  />
                </Field>

                <Field
                  label="Pilot"
                  hint={`Třída ${airship.airshipClass} vyžaduje licenci ${airship.airshipClass} nebo vyšší.`}
                  error={fieldErrors.pilotId?.[0]}
                >
                  <Select
                    value={pilotId ?? ''}
                    onChange={(event) =>
                      setPilotId(event.target.value === '' ? null : Number(event.target.value))
                    }
                  >
                    <option value="">Přiřadí operátor</option>
                    {pilots.data?.map((pilot) => (
                      <option key={pilot.id} value={pilot.id}>
                        {pilot.displayName} — licence {pilot.pilotLicenseClass}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="Účel letu" className="sm:col-span-2">
                  <Textarea
                    value={purpose}
                    onChange={(event) => setPurpose(event.target.value)}
                    placeholder="Firemní panoramatický let, svatba, reklamní přelet…"
                    maxLength={1000}
                  />
                </Field>
              </div>

              {parsed?.success === false && (
                <div className="mt-3">
                  <ErrorNote
                    title="Formulář ještě nesouhlasí s kontraktem"
                    detail={parsed.error.issues
                      .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
                      .join(' · ')}
                  />
                </div>
              )}
            </Card>
          )}

          {step === 3 && airship !== null && window !== null && (
            <Card>
              <CardHeader title="Souhrn" subtitle="Po vytvoření je rezervace koncept (DRAFT)." />
              <dl className="grid gap-2 sm:grid-cols-2">
                <Summary label="Vzducholoď" value={`${airship.name} (${airship.registration})`} />
                <Summary label="Třída / kapacita" value={`${airship.airshipClass} / ${String(airship.capacityPax)} pax`} />
                <Summary
                  label="Trasa"
                  value={`${hubs.data?.find((hub) => hub.id === departureHubId)?.code ?? '?'} → ${hubs.data?.find((hub) => hub.id === arrivalHubId)?.code ?? '?'}`}
                />
                <Summary label="Pasažérů" value={String(passengers)} />
                <Summary label="Začátek" value={dateTime(window.from)} />
                <Summary label="Konec" value={dateTime(window.to)} />
                <Summary label="Délka" value={duration(window.from, window.to)} />
                <Summary
                  label="Pilot"
                  value={
                    pilotId === null
                      ? 'přiřadí operátor'
                      : (pilots.data?.find((pilot) => pilot.id === pilotId)?.displayName ?? '—')
                  }
                />
              </dl>

              {purpose.trim() !== '' && (
                <p className="mt-3 text-[13px] text-[var(--text-secondary)]">
                  <span className="label-caps mr-2">Účel</span>
                  {purpose}
                </p>
              )}
            </Card>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => setStep((current) => Math.max(current - 1, 0))}
              disabled={step === 0}
            >
              ← Zpět
            </Button>

            {step < STEPS.length - 1 ? (
              <Button
                variant="primary"
                onClick={() => setStep((current) => current + 1)}
                disabled={!canAdvance()}
              >
                Pokračovat →
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={submit}
                loading={create.isPending}
                disabled={parsed?.success !== true}
              >
                Vytvořit rezervaci
              </Button>
            )}
          </div>
        </motion.div>

        {/* Price panel */}
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Cenová kalkulace" />
            {airship === null ? (
              <p className="text-[13px] text-[var(--text-muted)]">Nejdřív vyber vzducholoď.</p>
            ) : window === null ? (
              <p className="text-[13px] text-[var(--text-muted)]">Zadej termín pro výpočet ceny.</p>
            ) : priceQuote.isLoading ? (
              <Skeleton className="h-32" />
            ) : priceQuote.data ? (
              <>
                <dl className="flex flex-col gap-1.5 text-[13px]">
                  <Row
                    label={`Let ${String(priceQuote.data.billableHours)} h × ${money(priceQuote.data.hourlyRateCzk)}`}
                    value={moneyExact(priceQuote.data.flightCostCzk)}
                  />
                  <Row label="Pozemní obsluha" value={moneyExact(priceQuote.data.handlingFeeCzk)} />
                  <Row
                    label={`Pasažéři (${String(passengers)})`}
                    value={moneyExact(priceQuote.data.passengerFeeCzk)}
                  />
                </dl>
                <div className="mt-3 flex items-baseline justify-between border-t border-[var(--border-subtle)] pt-3">
                  <span className="label-caps">Celkem</span>
                  <span className="mono text-lg font-bold text-[var(--accent)]">
                    {moneyExact(priceQuote.data.totalCzk)}
                  </span>
                </div>
                <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                  Účtují se celé hodiny, minimálně 2 h. Storno: nad 72 h plná vratka, 24–72 h
                  polovina, pod 24 h nic.
                </p>
              </>
            ) : null}
          </Card>

          {airship !== null && (
            <Card>
              <CardHeader title="Pravidla, která se kontrolují" />
              <ul className="flex flex-col gap-1.5 text-[12px] text-[var(--text-secondary)]">
                <li>• {RULE_LABELS.RULE_OVERLAP}</li>
                <li>
                  • {RULE_LABELS.RULE_TURNAROUND}{' '}
                  <Pill tone="warn">{airship.turnaroundMinutes} min</Pill>
                </li>
                <li>• {RULE_LABELS.RULE_CAPACITY}</li>
                <li>• {RULE_LABELS.RULE_PILOT_LICENSE}</li>
                <li>• {RULE_LABELS.RULE_MAINTENANCE}</li>
                <li>• {RULE_LABELS.RULE_SLOT_BOUNDS}</li>
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-[var(--radius)] border border-[var(--border-subtle)] px-3 py-2">
      <dt className="text-[12px] text-[var(--text-muted)]">{label}</dt>
      <dd className="mono text-right text-[13px] font-medium">{value}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[var(--text-secondary)]">{label}</dt>
      <dd className="mono">{value}</dd>
    </div>
  );
}

export function AvailabilityVerdict({
  loading,
  available,
  violations,
  conflicts,
  turnaroundMinutes,
}: {
  loading: boolean;
  available: boolean | undefined;
  violations: Array<{ rule: keyof typeof RULE_LABELS; message: string; detail?: string }>;
  conflicts: Array<{ startAt: string; endAt: string; turnaroundBufferMinutes?: number }>;
  turnaroundMinutes: number;
}) {
  if (loading) {
    return <Skeleton className="h-14" />;
  }
  if (available === undefined) {
    return null;
  }
  if (available) {
    return (
      <div
        className="rounded-[var(--radius)] border px-3 py-2 text-[13px]"
        style={{ borderColor: 'var(--accent)', background: 'var(--accent-dim)' }}
        role="status"
      >
        <p className="font-medium text-[var(--accent)]">Termín je volný</p>
        <p className="mt-0.5 text-[12px] text-[var(--text-secondary)]">
          Včetně povinné pozemní obsluhy {turnaroundMinutes} min před i po letu.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-[var(--radius)] border px-3 py-2 text-[13px]"
      style={{ borderColor: 'var(--danger)', background: 'var(--danger-dim)' }}
      role="alert"
    >
      <p className="font-medium text-[var(--danger)]">Termín nelze rezervovat</p>
      <ul className="mt-1 flex flex-col gap-1">
        {violations.map((violation) => (
          <li key={violation.rule} className="text-[12px]">
            • {RULE_LABELS[violation.rule]}
            {violation.detail !== undefined && (
              <span className="mono ml-1 text-[11px] text-[var(--text-muted)]">
                ({violation.detail})
              </span>
            )}
          </li>
        ))}
      </ul>
      {conflicts.length > 0 && (
        <ul className="mono mt-2 flex flex-col gap-0.5 text-[11px] text-[var(--text-secondary)]">
          {conflicts.map((conflict, index) => (
            <li key={`${conflict.startAt}-${String(index)}`}>
              koliduje s {dateTime(conflict.startAt)} → {dateTime(conflict.endAt)}
              {conflict.turnaroundBufferMinutes !== undefined
                ? ` (+${String(conflict.turnaroundBufferMinutes)} min obsluha)`
                : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
